import { ArgentinaProvinceAnalytics } from "@/components/analytics/ArgentinaProvinceAnalytics";
import { CloudinaryImageTracker } from "@/components/analytics/CloudinaryImageTracker";
import { GoogleAnalyticsPageview } from "@/components/analytics/GoogleAnalyticsPageview";
import { useSearchReferrerTracking } from "@/hooks/useSearchReferrerTracking";
import { recordFirstVisitAttribution } from "@/lib/firstVisitAttribution";

// Captura referrer + landing URL del PRIMER pageview (antes de nav SPA).
// session_started → Slack lo dispara SessionIngressTracker (con geo + PostHog).
if (typeof window !== "undefined") {
  recordFirstVisitAttribution();
}

/** Wrapper componente — el hook necesita estar dentro de Routes para useLocation. */
function SearchReferrerTracker() {
  useSearchReferrerTracking();
  return null;
}
import { ClerkPostHogIdentify } from "@/components/analytics/ClerkPostHogIdentify";
import { ClerkDataFastIdentify } from "@/components/analytics/ClerkDataFastIdentify";
import { PostHogPageview } from "@/components/analytics/PostHogPageview";
import { SessionIngressTracker } from "@/components/analytics/SessionIngressTracker";
import { SessionRecordingEnforcer } from "@/components/analytics/SessionRecordingEnforcer";
import { PostHogProvider } from "@/components/analytics/PostHogProvider";
import { PostHogTrackingVerification } from "@/components/analytics/PostHogTrackingVerification";
import { ProvinceConversionTracker } from "@/components/analytics/ProvinceConversionTracker";
import { ErrorBoundary } from "@/components/ErrorBoundary";
import { LanguageProvider } from "@/components/LanguageProvider";
import { LanguageUrlSync } from "@/components/LanguageUrlSync";
import { RegionProvider } from "@/components/RegionProvider";
import { SkipLink } from "@/components/SkipLink";
import { StructuredData } from "@/components/StructuredData";
import { TemplateLoadingScreen } from "@/components/templates/TemplateLoadingScreen";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { Toaster } from "@/components/ui/toaster";
import { TooltipProvider } from "@/components/ui/tooltip";
import { CurrentTemplateProvider } from "@/contexts/CurrentTemplateContext";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { lazy, Suspense, useLayoutEffect, useState, useEffect, useRef } from "react";
import { isAdminAppPath } from "@/lib/adminAppPath";
import { isMembershipFunnelPath } from "@/lib/membershipFunnelEnglish";
import { BrowserRouter, Navigate, Outlet, Route, Routes, useLocation, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { AdminLayout } from "@/components/admin/AdminLayout";
import { ClerkProvider } from "@clerk/clerk-react";
import { useAuth, useUser } from "@clerk/clerk-react";
import { esES, enUS } from "@clerk/localizations";
import { dark } from "@clerk/themes";
import { useSessionClaims } from "@/hooks/useSessionClaims";
import { isProfileComplete, isPublicMetadataAdmin } from "@/lib/auth";
const GeneradorInvitaciones = lazy(() => import("./pages/admin/GeneradorInvitaciones"));
// Funnel / checkout / membership entry — eager (conversion-critical path)
import Index from "./pages/Index";
import NotFound from "./pages/NotFound";
import Gracias from "./pages/Gracias";
import GraciasPorTuCompra from "./pages/GraciasPorTuCompra";
import GraciasPorTuCompraLatam from "./pages/GraciasPorTuCompraLatam";
import ThankYou from "./pages/ThankYou";
import PremiumInviteSuccess from "./pages/PremiumInviteSuccess";
import CheckoutPage from "./pages/CheckoutPage";
import CheckoutArChannelInvite from "./pages/CheckoutArChannelInvite";
import CheckoutChannelInviteRouter from "./pages/CheckoutChannelInviteRouter";
import PagarPage from "./pages/PagarPage";
import ChannelInviteLanding from "./pages/ChannelInviteLanding";
import { MembershipLandingRoute } from "@/components/MembershipLandingRoute";
import { getMembershipLandingPath } from "@/lib/membershipLandingPath";
import MediaKitPasswordGate from "./components/MediaKitPasswordGate";
import MuxVideoCountryTest from "./components/dev/MuxVideoCountryTest";
import { TestRunner } from "./pages/TestRunner";
import ProtectedTestRoute from "./components/ProtectedTestRoute";
// SEO landings, portfolio, products — lazy (see lazyPublicPages.ts)
import {
  Argentina,
  Artista,
  Atleta,
  Audiovisual,
  BeautyExpert,
  Belleza,
  BestFriendInstagram,
  BestFriendInstagramSuccess,
  Biografia,
  BuenosAires,
  Contenido,
  ContentCreator,
  CookiePolicy,
  CountryLandingPage,
  CreadoraContenido,
  Creative,
  DarDeBaja,
  Deportista,
  Desnuda,
  DigitalCreator,
  DirectCheckout,
  DualCTAVideoLanding,
  DualCtaFunnelDashboard,
  Entrenamiento,
  EnvSetup,
  EnglishHome,
  Erome,
  Exclusivo,
  ExclusivoArSuccess,
  Fashion,
  Filtrado,
  Filtro,
  Fitness,
  Fotos,
  FotosDesnuda,
  FotosGratis,
  FullscreenExclusive,
  FullscreenExclusiveAr,
  FullscreenSorpresa,
  Galeria,
  GiftProduct,
  Gratis,
  Imagenes,
  IndexMinimal,
  Influencer,
  Instagram,
  InstagramMejoresAmigos,
  Invitacion,
  InvitacionArError,
  InvitacionArPending,
  InvitacionArSuccess,
  InvitacionExclusivaArgentina,
  InvitacionGrupo,
  Invite,
  InviteGenerator,
  Leaked,
  Lifestyle,
  MediaKitDesktop,
  MediaKitIntro,
  MembresiaPremiumVip,
  MembresiaTelegram,
  Modelo,
  Nude,
  Nutricion,
  Onlyfans,
  Pack,
  Pack1,
  Pack2,
  Pack3,
  Personalidad,
  Photography,
  Pornhub,
  Portfolio,
  Premium,
  PremiumInvite,
  Privado,
  PrivacyPolicy,
  ProductPageMockArs,
  ProductPageMockUsd,
  ProductPageSimple,
  ProductReel,
  ProductReelMockTest,
  Recetas,
  Reels,
  ReelSecretShare,
  Rutina,
  Skincare,
  SocialMedia,
  StripeValidation,
  SubscripcionesPlanes,
  TerminosYCondiciones,
  TestBaseTemplate,
  TestCTAButtons,
  TestCheckout,
  TestExclusivo,
  TestMuxCountry,
  TestNewTemplate,
  TestPage,
  TestPremiumArgentina,
  TestPremiumInternacional,
  TikTok,
  Tiktoker,
  TravelInfluencer,
  Viajes,
  Video,
  VideosDesnuda,
  VideosGratis,
  VipCustomPayment,
  Wellness,
  WhatsAppBroadcast,
  Xvideos,
  YouTube,
} from "./pages/lazyPublicPages";
// Admin pages — lazy-loaded para que el bundle inicial del sitio público
// NO incluya el código del admin. Saving: ~500KB del bundle de visitors que
// nunca abren /admin. Wrapping en Suspense con AdminRouteLoader fallback
// abajo en el route block.
const PortfolioAdmin = lazy(() => import("./pages/admin/PortfolioAdmin"));
const AudiovisualAdmin = lazy(() => import("./pages/admin/AudiovisualAdmin"));
const BaseTemplateAdmin = lazy(() => import("./pages/admin/BaseTemplateAdmin"));
const HeroCtaAdmin = lazy(() => import("./pages/admin/HeroCtaAdmin"));
const WorkerFlagsAdmin = lazy(() => import("./pages/admin/WorkerFlagsAdmin"));
const PremiumInviteAdmin = lazy(() => import("./pages/admin/PremiumInviteAdmin"));
const ReelsAdminHub = lazy(() => import("./pages/admin/reels/ReelsAdminHub"));
const ReelsOverviewAdmin = lazy(() => import("./pages/admin/reels/ReelsOverviewAdmin"));
const ReelsLayoutAdmin = lazy(() => import("./pages/admin/reels/ReelsLayoutAdmin"));
const ReelsPlaybackAdmin = lazy(() => import("./pages/admin/reels/ReelsPlaybackAdmin"));
const ReelsPlayerAdmin = lazy(() => import("./pages/admin/reels/ReelsPlayerAdmin"));
const ReelsPreviewAdmin = lazy(() => import("./pages/admin/reels/ReelsPreviewAdmin"));
const ReelsAnalyticsAdmin = lazy(() => import("./pages/admin/reels/ReelsAnalyticsAdmin"));
const ConfigHealthAdmin = lazy(() => import("./pages/admin/ConfigHealthAdmin"));
const MembershipApprovals = lazy(() => import("./pages/admin/MembershipApprovals"));
const UsersAdmin = lazy(() => import("./pages/admin/UsersAdmin"));
const AuditLogAdmin = lazy(() => import("./pages/admin/AuditLogAdmin"));
const FreeMemberApprovals = lazy(() => import("./pages/admin/FreeMemberApprovals"));
const StripeDisputesAdmin = lazy(() => import("./pages/admin/StripeDisputesAdmin"));
const StripePaymentsConfigAdmin = lazy(() => import("./pages/admin/StripePaymentsConfigAdmin"));
const StripeCheckoutAdmin = lazy(() => import("./pages/admin/StripeCheckoutAdmin"));
const RecentReportAdmin = lazy(() => import("./pages/admin/RecentReportAdmin"));
const AnalyticsHub = lazy(() => import("./pages/admin/AnalyticsHub"));

const SeoGrowthAdmin = lazy(() => import("./pages/admin/SeoGrowthAdmin"));
const CountryTemplateAdmin = lazy(() => import("./pages/admin/CountryTemplateAdmin"));
const CountryControlAdmin = lazy(() => import("./pages/admin/CountryControlAdmin"));
const CityControlAdmin = lazy(() => import("./pages/admin/CityControlAdmin"));
const UsStateControlAdmin = lazy(() => import("./pages/admin/UsStateControlAdmin"));
const ArCityMapAdmin = lazy(() => import("./pages/admin/ArCityMapAdmin"));
const DualCtaVariantsAdmin = lazy(() => import("./pages/admin/DualCtaVariantsAdmin"));
const DualCtaPreviewAdmin = lazy(() => import("./pages/admin/DualCtaPreviewAdmin"));
const DualCtaVisibilityAdmin = lazy(() => import("./pages/admin/DualCtaVisibilityAdmin"));
const CountryLandingsAdmin = lazy(() => import("./pages/admin/CountryLandingsAdmin"));
const BaProvinceReportAdmin = lazy(() => import("./pages/admin/BaProvinceReportAdmin"));
const MercadoPagoPlansAdmin = lazy(() => import("./pages/admin/MercadoPagoPlansAdmin"));
const SidebarConfigAdmin = lazy(() => import("./pages/admin/SidebarConfigAdmin"));
const RevenueAdmin = lazy(() => import("./pages/admin/RevenueAdmin"));
const InvestorKpiAdmin = lazy(() => import("./pages/admin/InvestorKpiAdmin"));
const FinanzasPersonalesAdmin = lazy(() => import("./pages/admin/FinanzasPersonalesAdmin"));
const AdminDashboard = lazy(() => import("./pages/admin/AdminDashboard"));
import Registro from "./pages/Registro";
import PortalRegistro from "./pages/PortalRegistro";
import PortalCompleteProfile from "./pages/PortalCompleteProfile";
import CompleteRegistration from "./pages/CompleteRegistration";
import PendingApproval from "./pages/PendingApproval";
import MembershipAccessPortal from "./pages/MembershipAccessPortal";
import { MEMBERSHIP_ACCESS_PORTAL_PATH, MEMBERSHIP_ROUTE } from "@/lib/membershipAccessFlow";
import { ProtectedAdminRoute } from "./components/ProtectedAdminRoute";
import AdminGeoSimulatorMount from "./components/admin/AdminGeoSimulatorMount";
import {
  getClerkPublishableKey,
  isLikelyClerkPublishableKey,
} from "@/lib/clerkPublishableKey";
import { BRAND_AUTH_LOGO_URL, BRAND_PERSON_NAME, BRAND_NAME } from "@/lib/brandConfig";

const queryClient = new QueryClient();

/**
 * index.html incluye #seo-static-snapshot para crawlers sin JS. Con React activo el pie real es el de cada
 * plantilla (p. ej. BaseTemplateFooter); si no removemos el snapshot, hay dos footers duplicados.
 */
function RemoveSeoStaticSnapshot() {
  useLayoutEffect(() => {
    document.getElementById("seo-static-snapshot")?.remove();
  }, []);
  return null;
}

/** Alias legacy (/pending-approval, /mi-acceso) → /portal, preservando la query. */
function PortalRedirect() {
  const { search } = useLocation();
  return <Navigate to={`${MEMBERSHIP_ROUTE.PORTAL}${search}`} replace />;
}

/**
 * Legacy `/video-landing` → ruta canónica de membership (AR `/ser-miembro`,
 * intl `/membership`), preservando query + hash.
 */
function LegacyVideoLandingRedirect() {
  const { search, hash } = useLocation();
  return <Navigate to={`${getMembershipLandingPath()}${search}${hash}`} replace />;
}

function isPendingApprovalPath(pathname: string): boolean {
  return (
    pathname === MEMBERSHIP_ROUTE.PORTAL ||
    pathname === MEMBERSHIP_ROUTE.PENDING_APPROVAL ||
    pathname === MEMBERSHIP_ROUTE.MI_ACCESO
  );
}

function isProfileCompletionPath(pathname: string): boolean {
  return (
    pathname === MEMBERSHIP_ROUTE.COMPLETAR_REGISTRO ||
    pathname === "/portal/complete-profile" ||
    pathname === "/portal/registro" ||
    pathname === MEMBERSHIP_ROUTE.REGISTRO ||
    pathname === MEMBERSHIP_ROUTE.ENTRAR
  );
}

/**
 * Hard gate: signed-in members pending approval can only see the pending portal.
 * Source of truth: Clerk session claims (fallback to publicMetadata while claims hydrate).
 */
function PendingApprovalAccessGate({ children }: { children: React.ReactNode }) {
  const { pathname, search } = useLocation();
  const { isLoaded, isSignedIn } = useAuth();
  const { user } = useUser();
  const claims = useSessionClaims();

  if (!isLoaded || !isSignedIn) return <>{children}</>;

  const meta = (user?.publicMetadata ?? {}) as Record<string, unknown>;
  const isAdmin =
    claims.role === "admin" ||
    claims.role === "super_admin" ||
    claims.isSuperAdmin ||
    isPublicMetadataAdmin(meta);
  if (isAdmin) return <>{children}</>;

  const approvedByMeta = meta.approved === true;
  const approved = claims.approved || approvedByMeta;
  if (approved) return <>{children}</>;

  // New signed-in users can still need profile completion before they reach pending approval.
  if (!isProfileComplete(user)) {
    if (isProfileCompletionPath(pathname)) return <>{children}</>;
    return <Navigate to={MEMBERSHIP_ROUTE.COMPLETAR_REGISTRO} replace />;
  }

  if (isPendingApprovalPath(pathname)) return <>{children}</>;
  return <Navigate to={`${MEMBERSHIP_ROUTE.PORTAL}${search}`} replace />;
}

// Test routes (test-mux-video, test-mux-country, test-checkout, etc.) solo activas con VITE_ENABLE_TEST_ROUTES=true
const TEST_ROUTES_ENABLED = import.meta.env.VITE_ENABLE_TEST_ROUTES === "true";

// Clerk: VITE_ / NEXT_PUBLIC_ en cliente; build también puede mapear CLERK_PUBLISHABLE_KEY (ver vite.config.ts).
const CLERK_PUBLISHABLE_KEY = getClerkPublishableKey();

if (import.meta.env.DEV && CLERK_PUBLISHABLE_KEY && !isLikelyClerkPublishableKey(CLERK_PUBLISHABLE_KEY)) {
  console.warn(
    "[Clerk] La publishable key no parece válida (esperado pk_test_… o pk_live_…). Revisa variables en .env / Railway."
  );
}

/**
 * Clerk debe vivir **dentro** de BrowserRouter y recibir routerPush/routerReplace;
 * si no, useAuth puede quedar en isLoaded=false y RedirectToSignIn no muestra login (Clerk v5 + React Router).
 */
function AppClerkProvider({ children }: { children: React.ReactNode }) {
  const navigate = useNavigate();
  const { pathname, search } = useLocation();
  const afterAuthUrl = `${pathname}${search}` || "/admin";

  if (!CLERK_PUBLISHABLE_KEY) {
    return <>{children}</>;
  }

  const authLogoUrl = BRAND_AUTH_LOGO_URL.trim();
  const clerkLayout = {
    socialButtonsVariant: "blockButton" as const,
    ...( /^https?:\/\//i.test(authLogoUrl)
      ? { logoImageUrl: authLogoUrl, logoPlacement: "inside" as const }
      : {} ),
  };

  return (
    <ClerkProvider
      publishableKey={CLERK_PUBLISHABLE_KEY}
      routerPush={(to) => {
        void navigate(to);
      }}
      routerReplace={(to) => {
        void navigate(to, { replace: true });
      }}
      signInFallbackRedirectUrl={afterAuthUrl}
      signUpFallbackRedirectUrl={afterAuthUrl}
      localization={getClerkLocale(pathname)}
      appearance={{
        baseTheme: dark,
        layout: clerkLayout,
        variables: {
          colorPrimary: "hsl(var(--primary))",
          colorBackground: "hsl(240, 10%, 10%)",
          colorText: "hsl(240, 5%, 95%)",
          colorTextSecondary: "hsl(240, 5%, 72%)",
          colorInputBackground: "hsl(240, 8%, 16%)",
          colorInputText: "hsl(240, 5%, 95%)",
          colorNeutral: "hsl(240, 5%, 95%)",
          borderRadius: "var(--radius)",
        },
      }}
    >
      {children}
    </ClerkProvider>
  );
}

// Clerk locale: membership funnel always EN; otherwise browser default.
function getClerkLocale(pathname: string): typeof esES | typeof enUS {
  if (isMembershipFunnelPath(pathname)) return enUS;
  if (typeof window !== "undefined") {
    const browserLang = navigator.language || "";
    if (browserLang.startsWith("es")) return esES;
  }
  return enUS;
}

// Loading component while i18n initializes
//
// UX strategy:
//   1. Splash visible IMMEDIATE (zero flash of unstyled content)
//   2. Min show time MIN_SPLASH_MS para evitar flicker si carga muy rápido
//      — un splash de 30ms es peor que ninguno (parpadea)
//   3. Auto-dismiss en cuanto i18n.isInitialized AND tiempo min cumplido
//   4. Force fallback a 3s si i18n hangea
//   5. Fade-out smooth — splash NO desaparece de golpe; sale con opacity
//      transition para que el visitor vea continuidad visual
const MIN_SPLASH_MS = 150;
const FORCE_TIMEOUT_MS = 3000;
const FADE_OUT_MS = 280;

function I18nLoadingWrapper({ children }: { children: React.ReactNode }) {
  const { i18n } = useTranslation();
  const { pathname } = useLocation();
  const skipSplash = isAdminAppPath(pathname);
  const [showContent, setShowContent] = useState(skipSplash);
  const [splashVisible, setSplashVisible] = useState(!skipSplash);
  const mountedAt = useRef<number>(Date.now());

  useEffect(() => {
    if (skipSplash) {
      setShowContent(true);
      setSplashVisible(false);
    }
  }, [skipSplash]);

  useEffect(() => {
    // Force fallback: si i18n cuelga 3s, igual mostramos content.
    const forceTimer = setTimeout(() => {
      if (!showContent) {
        console.warn("[I18n] Timeout, forcing content display");
        setShowContent(true);
      }
    }, FORCE_TIMEOUT_MS);
    return () => clearTimeout(forceTimer);
  }, [showContent]);

  useEffect(() => {
    if (!i18n.isInitialized || !i18n.language) return;
    // Min show time: si i18n cargó en 30ms, esperamos a cumplir MIN_SPLASH_MS
    // para que el splash no parpadee. Si cargó en 600ms, ya está cumplido
    // y mostramos content de inmediato.
    const elapsed = Date.now() - mountedAt.current;
    const wait = Math.max(0, MIN_SPLASH_MS - elapsed);
    const timer = setTimeout(() => setShowContent(true), wait);
    return () => clearTimeout(timer);
  }, [i18n.isInitialized, i18n.language]);

  useEffect(() => {
    if (!showContent) return;
    // Showcontent → arrancamos fade-out del splash. Después de FADE_OUT_MS
    // el splash se unmonta del DOM. El children ya está renderizado debajo
    // (por eso lo renderizamos en paralelo), así que la transición es seamless.
    const fadeTimer = setTimeout(() => setSplashVisible(false), FADE_OUT_MS);
    return () => clearTimeout(fadeTimer);
  }, [showContent]);

  return (
    <>
      {splashVisible && <TemplateLoadingScreen fadingOut={showContent} />}
      {showContent && children}
    </>
  );
}

// Loader minimal para Suspense de admin routes lazy-loaded. Consistente con
// el design system (#0a0a0a base + amber accent). No usa el splash de boot
// porque el visitor ya tiene contexto admin (ya pasó auth Clerk + AdminLayout
// header está visible). Solo necesitamos llenar el container donde renderiza
// la route — un skeleton sutil.
function AdminRouteLoader() {
  return (
    <div
      className="flex flex-col items-center justify-center min-h-[60vh] bg-[#0a0a0a]"
      role="status"
      aria-live="polite"
      aria-label="Cargando sección admin"
    >
      <div className="flex items-center gap-2">
        <span
          className="inline-block h-1.5 w-1.5 rounded-full bg-amber-400/80"
          style={{
            animation: "admin-loader-dot 1.2s ease-in-out infinite both",
            animationDelay: "0ms",
          }}
        />
        <span
          className="inline-block h-1.5 w-1.5 rounded-full bg-amber-400/80"
          style={{
            animation: "admin-loader-dot 1.2s ease-in-out infinite both",
            animationDelay: "160ms",
          }}
        />
        <span
          className="inline-block h-1.5 w-1.5 rounded-full bg-amber-400/80"
          style={{
            animation: "admin-loader-dot 1.2s ease-in-out infinite both",
            animationDelay: "320ms",
          }}
        />
      </div>
      <p className="mt-3 text-[10px] uppercase tracking-[0.3em] text-zinc-500">
        Cargando
      </p>
      <style>{`
        @keyframes admin-loader-dot {
          0%, 80%, 100% { opacity: 0.25; transform: scale(0.8); }
          40% { opacity: 1; transform: scale(1); }
        }
        @media (prefers-reduced-motion: reduce) {
          [aria-label="Cargando sección admin"] span {
            animation-duration: 2s !important;
          }
        }
      `}</style>
    </div>
  );
}

const App = () => {
  if (!CLERK_PUBLISHABLE_KEY) {
    console.error(
      "[Clerk] Falta publishable key. Define VITE_CLERK_PUBLISHABLE_KEY o NEXT_PUBLIC_CLERK_PUBLISHABLE_KEY (o CLERK_PUBLISHABLE_KEY en el build) y redeploy. Sin eso, /admin no puede usar Clerk."
    );
  }

  const appContent = (
    <ErrorBoundary>
      <QueryClientProvider client={queryClient}>
        <PostHogProvider>
          <TooltipProvider>
            <Toaster />
            <Sonner />
            <RegionProvider>
              <LanguageProvider>
                <BrowserRouter>
                  <AppClerkProvider>
                  <RemoveSeoStaticSnapshot />
                  <CurrentTemplateProvider>
                    <I18nLoadingWrapper>
                      <LanguageUrlSync />
                      <SkipLink />
                      <ArgentinaProvinceAnalytics />
                      <ProvinceConversionTracker />
                      <CloudinaryImageTracker />
                      <StructuredData />
                      <AdminGeoSimulatorMount />
                      <PendingApprovalAccessGate>
                      <Routes>
                    <Route path="/" element={<Index />} />
                    <Route path="/minimal" element={<IndexMinimal />} />
                    {/* /test — réplica de la home Argentina (BaseTemplate) con CTA buttons de prueba */}
                    <Route path="/test" element={<TestCTAButtons />} />
                    {/* Rutas de test desactivadas por defecto; solo con VITE_ENABLE_TEST_ROUTES=true */}
                    {TEST_ROUTES_ENABLED ? (
                      <>
                        <Route path="/test-runner" element={<ProtectedTestRoute><TestRunner /></ProtectedTestRoute>} />
                        <Route path="/test-mux-country" element={<ProtectedTestRoute><TestMuxCountry /></ProtectedTestRoute>} />
                        <Route path="/test-mux-video" element={<ProtectedTestRoute><MuxVideoCountryTest /></ProtectedTestRoute>} />
                        <Route path="/test-checkout" element={<ProtectedTestRoute><TestCheckout /></ProtectedTestRoute>} />
                        <Route path="/validate-stripe" element={<ProtectedTestRoute><StripeValidation /></ProtectedTestRoute>} />
                      </>
                    ) : (
                      <>
                        <Route path="/test-runner" element={<Navigate to="/" replace />} />
                        <Route path="/test-mux-country" element={<Navigate to="/" replace />} />
                        <Route path="/test-mux-video" element={<Navigate to="/" replace />} />
                        <Route path="/test-checkout" element={<Navigate to="/" replace />} />
                        <Route path="/validate-stripe" element={<Navigate to="/" replace />} />
                      </>
                    )}
                    {/* Admin shell: sidebar para /admin/*, /dashboard/* y rutas de utilidad */}
                    <Route path="/admin/" element={<Navigate to="/admin" replace />} />
                    <Route
                      element={
                        <ProtectedAdminRoute title="Panel de Administración">
                          <Suspense fallback={<AdminRouteLoader />}>
                            <AdminLayout />
                          </Suspense>
                        </ProtectedAdminRoute>
                      }
                    >
                      <Route path="admin" element={<Outlet />}>
                        <Route index element={<AdminDashboard />} />
                        <Route path="site-settings" element={<BaseTemplateAdmin />} />
                        <Route path="invitaciones" element={<GeneradorInvitaciones />} />
                        <Route path="invitaciones/" element={<Navigate to="/admin/invitaciones" replace />} />
                        <Route path="premium-invite-config" element={<PremiumInviteAdmin />} />
                        {/* Reels hub: sub-páginas lazy por capacidad (performance) */}
                        <Route path="reels" element={<ReelsAdminHub />}>
                          <Route index element={<ReelsOverviewAdmin />} />
                          <Route path="layout" element={<ReelsLayoutAdmin />} />
                          <Route path="playback" element={<ReelsPlaybackAdmin />} />
                          <Route path="player" element={<ReelsPlayerAdmin />} />
                          <Route path="preview" element={<ReelsPreviewAdmin />} />
                          <Route path="analytics" element={<ReelsAnalyticsAdmin />} />
                        </Route>
                        <Route path="portfolio" element={<PortfolioAdmin />} />
                        <Route path="audiovisual" element={<AudiovisualAdmin />} />
                        <Route path="hero-cta" element={<HeroCtaAdmin />} />
                        {/* Compat: deeplinks/bookmarks legacy a /admin/wishlist-config
                            redirigen al nombre nuevo. La ruta vieja pierde el "/admin"
                            prefix porque ya estamos dentro del wrapper /admin/*. */}
                        <Route path="wishlist-config" element={<Navigate to="/admin/hero-cta" replace />} />
                        <Route path="country-template" element={<CountryTemplateAdmin />} />
                        <Route path="attribution" element={<Navigate to="/admin/analytics?tab=attribution" replace />} />
                        <Route path="seo-growth" element={<SeoGrowthAdmin />} />
                        <Route path="ar-city-map" element={<ArCityMapAdmin />} />
                        <Route path="dual-cta-variants" element={<DualCtaVariantsAdmin />} />
                        {/* Full-page editor (reemplaza modal). Mismo componente: el editor
                           detecta /new vs /edit/:variantId vía useParams y muestra el form
                           en lugar de los tabs. URL compartible, browser back funciona. */}
                        <Route path="dual-cta-variants/new" element={<DualCtaVariantsAdmin />} />
                        <Route path="dual-cta-variants/edit/:variantId" element={<DualCtaVariantsAdmin />} />
                        <Route path="preview/dual" element={<DualCtaPreviewAdmin />} />
                        <Route path="dual-cta-visibility" element={<DualCtaVisibilityAdmin />} />
                        <Route path="country-landings" element={<CountryLandingsAdmin />} />
                        <Route path="ba-province-report" element={<BaProvinceReportAdmin />} />
                        <Route path="worker-flags" element={<WorkerFlagsAdmin />} />
                        <Route path="country-control" element={<CountryControlAdmin />} />
                        <Route path="cities" element={<CityControlAdmin />} />
                        <Route path="us-states" element={<UsStateControlAdmin />} />
                        <Route path="config-health" element={<ConfigHealthAdmin />} />
                        <Route path="memberships" element={<MembershipApprovals />} />
                        <Route path="users" element={<UsersAdmin />} />
                        <Route path="audit-log" element={<AuditLogAdmin />} />
                        <Route path="free-members" element={<FreeMemberApprovals />} />
                        <Route path="revenue" element={<RevenueAdmin />} />
                        <Route path="investor-kpi" element={<InvestorKpiAdmin />} />
                        <Route path="finanzas-personales" element={<FinanzasPersonalesAdmin />} />
                        <Route path="stripe-disputes" element={<StripeDisputesAdmin />} />
                        <Route path="stripe-payments-config" element={<StripePaymentsConfigAdmin />} />
                        <Route path="stripe-checkout" element={<StripeCheckoutAdmin />} />
                        <Route path="report-recent" element={<Navigate to="/admin/analytics?tab=report" replace />} />
                        <Route path="analytics" element={<AnalyticsHub />} />
                        <Route path="mercadopago-plans" element={<MercadoPagoPlansAdmin />} />
                        <Route path="sidebar-config" element={<SidebarConfigAdmin />} />
                        {/* Rutas viejas redirigen al hub unificado /admin/analytics.
                            La dashboard tab abre por defecto; user puede click
                            cualquier tab (metrics / mux / report) para deep-dive.
                            Preservado en App para evitar 404 en bookmarks. */}
                        <Route path="mux-video-analytics" element={<Navigate to="/admin/analytics?tab=mux" replace />} />
                        <Route path="front-metrics" element={<Navigate to="/admin/analytics?tab=metrics" replace />} />
                      </Route>
                      <Route path="dashboard" element={<Outlet />}>
                        <Route path="subscripciones/planes" element={<SubscripcionesPlanes />} />
                        <Route path="env-setup" element={<EnvSetup />} />
                        <Route path="vercel-setup" element={<Navigate to="/dashboard/env-setup" replace />} />
                        <Route path="dual-cta-funnel" element={<DualCtaFunnelDashboard />} />
                      </Route>
                      <Route path="test-base-template" element={<TestBaseTemplate />} />
                      <Route path="test-cta-buttons" element={<TestCTAButtons />} />
                    </Route>
                    <Route path="/gift/:slug" element={<GiftProduct />} />
                    <Route path="/p/mock-usd" element={<ProductPageMockUsd />} />
                    <Route path="/p/mock-ars" element={<ProductPageMockArs />} />
                    <Route path="/p/:slug" element={<ProductPageSimple />} />
                    <Route path="/buy/:slug" element={<DirectCheckout />} />
                    <Route path="/reel/:slug" element={<ProductReel />} />
                    <Route path="/test/reel/:slug" element={<ProductReelMockTest />} />
                    <Route
                      path="/canal"
                      element={<ChannelInviteLanding />}
                    />
                    <Route
                      path="/channel"
                      element={<ChannelInviteLanding />}
                    />
                    <Route
                      path="/checkout-channel-invite-router"
                      element={<CheckoutChannelInviteRouter />}
                    />
                    <Route
                      path="/checkout-channel-invite"
                      element={<CheckoutPage />}
                    />
                    <Route
                      path="/checkout-ar-channel-invite"
                      element={<CheckoutArChannelInvite />}
                    />
                    <Route path="/pagar/:paymentIntentId" element={<PagarPage />} />
                    <Route path="/generator" element={<InviteGenerator />} />
                    <Route path="/invitacion" element={<PremiumInvite />} />
                    <Route
                      path="/invitacion-mejores-amigos"
                      element={<InvitacionGrupo />}
                    />
                    <Route
                      path="/grupo-privado"
                      element={<InvitacionGrupo />}
                    />
                    <Route path="/invite" element={<Invite />} />
                    <Route
                      path="/creadora-de-contenido"
                      element={<CreadoraContenido />}
                    />
                    <Route
                      path="/content-creator"
                      element={<ContentCreator />}
                    />
                    <Route path="/influencer" element={<Influencer />} />
                    <Route path="/en" element={<EnglishHome />} />
                    <Route path="/fotos" element={<Fotos />} />
                    <Route path="/imagenes" element={<Imagenes />} />
                    <Route path="/reels" element={<Reels />} />
                    {/* Secret-share invitation: peek → Ver ahora → open=1 (anti-unfurl) */}
                    <Route path="/reels/:token" element={<ReelSecretShare />} />
                    {/* /videos page removed (off-brand asset) — redirect to home so
                        external links and SEO don't 404. */}
                    <Route path="/videos" element={<Navigate to="/" replace />} />
                    <Route path="/video" element={<Video />} />
                    <Route path="/lifestyle" element={<Lifestyle />} />
                    <Route path="/nutricion" element={<Nutricion />} />
                    <Route path="/belleza" element={<Belleza />} />
                    <Route path="/viajes" element={<Viajes />} />
                    <Route path="/tiktok" element={<TikTok />} />
                    <Route path="/instagram" element={<Instagram />} />
                    <Route path="/youtube" element={<YouTube />} />
                    <Route path="/erome" element={<Erome />} />
                    <Route path="/filtrados" element={<Filtrado />} />
                    <Route path="/filtro" element={<Filtro />} />
                    <Route path="/leaked" element={<Leaked />} />
                    <Route path="/onlyfans" element={<Onlyfans />} />
                    <Route path="/pack" element={<Pack />} />
                    <Route path="/pack1" element={<Pack1 />} />
                    <Route path="/pack2" element={<Pack2 />} />
                    <Route path="/pack3" element={<Pack3 />} />
                    <Route path="/desnuda" element={<Desnuda />} />
                    <Route path="/nude" element={<Nude />} />
                    <Route path="/premium" element={<Premium />} />
                    <Route path="/privado" element={<Privado />} />
                    <Route path="/contenido" element={<Contenido />} />
                    <Route path="/exclusivo" element={<Exclusivo />} />
                    <Route path="/gratis" element={<Gratis />} />
                    <Route path="/galeria" element={<Galeria />} />
                    <Route path="/fotos-desnuda" element={<FotosDesnuda />} />
                    <Route path="/videos-desnuda" element={<VideosDesnuda />} />
                    <Route path="/pornhub" element={<Pornhub />} />
                    <Route path="/xvideos" element={<Xvideos />} />
                    <Route path="/fotos-gratis" element={<FotosGratis />} />
                    <Route path="/videos-gratis" element={<VideosGratis />} />
                    <Route path="/tiktoker" element={<Tiktoker />} />
                    <Route path="/modelo" element={<Modelo />} />
                    <Route path="/photography" element={<Photography />} />
                    <Route path="/artista" element={<Artista />} />
                    <Route path="/fashion" element={<Fashion />} />
                    <Route path="/wellness" element={<Wellness />} />
                    <Route
                      path="/digital-creator"
                      element={<DigitalCreator />}
                    />
                    <Route path="/social-media" element={<SocialMedia />} />
                    <Route path="/personalidad" element={<Personalidad />} />
                    <Route path="/beauty-expert" element={<BeautyExpert />} />
                    <Route
                      path="/travel-influencer"
                      element={<TravelInfluencer />}
                    />
                    <Route path="/creative" element={<Creative />} />
                    <Route path="/argentina" element={<Argentina />} />
                    <Route path="/ar/invitacion" element={<InvitacionExclusivaArgentina />} />
                    <Route path="/ar/invite" element={<InvitacionExclusivaArgentina />} />
                    {/* Checkout AR result pages - redirect a Telegram */}
                    <Route path="/ar/invitacion/success" element={<InvitacionArSuccess />} />
                    <Route path="/ar/invitacion/error" element={<InvitacionArError />} />
                    <Route path="/ar/invitacion/pending" element={<InvitacionArPending />} />

                    {/* Flujo VIP: registro forzado → completar perfil → /portal (espera + acceso) */}
                    {/*
                     * /portal/complete-profile — entry point post-pago (DualCTAVideoLanding + all variants).
                     * Stripe + MP redirigen aquí (DEFAULT_SUCCESS_PATH en muchasGraciasSuccessUrl.ts).
                     * Form: email, telegram, cumpleaños (obligatorios) + nombre (opcional).
                     * Clerk signUp (passwordless) → email code → /portal (pending approval).
                     */}
                    <Route path="/portal/complete-profile" element={<PortalCompleteProfile />} />
                    {/*
                     * /portal/registro — legacy entry point. Redirect a /portal/complete-profile
                     * preservando query (cubre planes MP con back_url=.../portal/registro configurado
                     * en el panel de MercadoPago antes del cambio de DEFAULT_SUCCESS_PATH).
                     */}
                    <Route path="/portal/registro" element={<PortalRegistro />} />
                    <Route path={MEMBERSHIP_ROUTE.REGISTRO} element={<Registro mode="sign-up" />} />
                    <Route path={MEMBERSHIP_ROUTE.ENTRAR} element={<Registro mode="sign-in" />} />
                    <Route path={MEMBERSHIP_ROUTE.COMPLETAR_REGISTRO} element={<CompleteRegistration />} />
                    {/* Página única: datos del usuario + espera + link privado al aprobar. */}
                    <Route path={MEMBERSHIP_ROUTE.PORTAL} element={<PendingApproval />} />
                    {/* Legacy → /portal (preservando query). */}
                    <Route path={MEMBERSHIP_ROUTE.PENDING_APPROVAL} element={<PortalRedirect />} />
                    <Route path={MEMBERSHIP_ROUTE.MI_ACCESO} element={<PortalRedirect />} />
                    <Route path={MEMBERSHIP_ACCESS_PORTAL_PATH} element={<MembershipAccessPortal />} />
                    {/* Invitación global: video landing dual-CTA (intl→Stripe primarily).
                        AR visitors (country=AR) are redirected to BaseTemplate (/) unless super admin
                        (for testing). See guard inside DualCTAVideoLanding.
                        startVipMembershipVideoCheckout detecta esta ruta (flow premium_invite). */}
                    <Route path="/invitacion-premium" element={<DualCTAVideoLanding />} />
                    
                    {/* Membresía VIP exclusiva — ignora la desactivación del plan VIP */}
                    <Route path="/membresia-vip" element={<MembresiaPremiumVip />} />
                    
                    {/* Test pages para desarrolladores — sin restricciones */}
                    <Route path="/test-new-template" element={<TestNewTemplate />} />
                    <Route path="/test-premium-argentina" element={<TestPremiumArgentina />} />
                    <Route path="/test-premium-internacional" element={<TestPremiumInternacional />} />
                    <Route path="/premium-invite" element={<PremiumInvite />} />
                    <Route path="/muchas-gracias" element={<PremiumInviteSuccess />} />
                    <Route path="/invitacion-premium/success" element={<PremiumInviteSuccess />} />
                    <Route path="/buenos-aires" element={<BuenosAires />} />
                    <Route path="/deportista" element={<Deportista />} />
                    <Route path="/fitness" element={<Fitness />} />
                    <Route path="/entrenamiento" element={<Entrenamiento />} />
                    <Route path="/atleta" element={<Atleta />} />
                    <Route path="/sobre-mi" element={<Navigate to="/" replace />} />
                    <Route path="/biografia" element={<Biografia />} />
                    {/* LATAM expansion landings (Ahrefs data-driven KD=0,
                        ver src/config/countryLandings.ts). 5 MX + 5 CO. */}
                    <Route path="/famosas-mexicanas" element={<CountryLandingPage slug="famosas-mexicanas" />} />
                    <Route path="/influencer-mexicana" element={<CountryLandingPage slug="influencer-mexicana" />} />
                    <Route path="/tiktoker-mexicana" element={<CountryLandingPage slug="tiktoker-mexicana" />} />
                    <Route path="/modelos-mexicanas" element={<CountryLandingPage slug="modelos-mexicanas" />} />
                    <Route path="/modelo-mexicana" element={<CountryLandingPage slug="modelo-mexicana" />} />
                    <Route path="/modelo-colombiana" element={<CountryLandingPage slug="modelo-colombiana" />} />
                    <Route path="/modelos-colombianas" element={<CountryLandingPage slug="modelos-colombianas" />} />
                    <Route path="/influencer-colombiana" element={<CountryLandingPage slug="influencer-colombiana" />} />
                    <Route path="/famosas-colombianas" element={<CountryLandingPage slug="famosas-colombianas" />} />
                    <Route path="/tiktoker-colombiana" element={<CountryLandingPage slug="tiktoker-colombiana" />} />
                    {/* Slugs detectados via Ahrefs search-suggestions
                        — plural mejor TP que singular; masculino captura
                        vol 7x mayor que femenino. Detalle en commit msg. */}
                    <Route path="/tiktoker-mexicanas" element={<CountryLandingPage slug="tiktoker-mexicanas" />} />
                    <Route path="/influencer-mexicanas" element={<CountryLandingPage slug="influencer-mexicanas" />} />
                    <Route path="/creador-de-contenido" element={<CountryLandingPage slug="creador-de-contenido" />} />
                    <Route path="/creadores-de-contenido" element={<CountryLandingPage slug="creadores-de-contenido" />} />
                    {/* US English landings — Ahrefs US data (KD≤1):
                        latina-models top TP 3700. Bilingual brand strategy. */}
                    <Route path="/latina-models" element={<CountryLandingPage slug="latina-models" />} />
                    <Route path="/latina-influencer" element={<CountryLandingPage slug="latina-influencer" />} />
                    <Route path="/hispanic-influencer" element={<CountryLandingPage slug="hispanic-influencer" />} />
                    <Route path="/argentinian-model" element={<CountryLandingPage slug="argentinian-model" />} />
                    <Route path="/argentinian-influencer" element={<CountryLandingPage slug="argentinian-influencer" />} />
                    <Route path="/latina-content-creator" element={<CountryLandingPage slug="latina-content-creator" />} />
                    {/* US UGC cluster — Ahrefs mig 038 (ugc creator 21k vol) */}
                    <Route path="/ugc-creator" element={<CountryLandingPage slug="ugc-creator" />} />
                    <Route path="/what-is-ugc-creator" element={<Navigate to="/what-is-a-ugc-creator" replace />} />
                    <Route path="/content-creator-jobs" element={<CountryLandingPage slug="content-creator-jobs" />} />
                    <Route path="/how-to-become-instagram-influencer" element={<Navigate to="/how-to-become-an-influencer" replace />} />
                    <Route path="/become-a-ugc-creator" element={<Navigate to="/how-to-become-a-ugc-creator" replace />} />
                    <Route path="/what-is-a-ugc-creator" element={<CountryLandingPage slug="what-is-a-ugc-creator" />} />
                    <Route path="/how-to-become-a-ugc-creator" element={<CountryLandingPage slug="how-to-become-a-ugc-creator" />} />
                    <Route path="/ugc-creator-meaning" element={<Navigate to="/what-is-a-ugc-creator" replace />} />
                    <Route path="/how-to-become-an-influencer" element={<CountryLandingPage slug="how-to-become-an-influencer" />} />
                    {/* Argentina — Ahrefs AR KD=0 */}
                    <Route path="/influencer-argentina" element={<CountryLandingPage slug="influencer-argentina" />} />
                    <Route path="/influencer-argentina-tiktok" element={<CountryLandingPage slug="influencer-argentina-tiktok" />} />
                    <Route path="/como-ser-influencer" element={<CountryLandingPage slug="como-ser-influencer" />} />
                    <Route path="/contacto" element={<Navigate to="/" replace />} />
                    <Route path="/rutina" element={<Rutina />} />
                    <Route path="/recetas" element={<Recetas />} />
                    <Route path="/skincare" element={<Skincare />} />
                    <Route
                      path="/gracias-por-tu-compra"
                      element={<GraciasPorTuCompra />}
                    />
                    <Route
                      path="/gracias-por-tu-compra-latam"
                      element={<GraciasPorTuCompraLatam />}
                    />
                    {/* Thank you pages: required for Stripe Elements custom → validate purchase & Telegram entry button */}
                    <Route path="/gracias" element={<Gracias />} />
                    <Route path="/thank-you" element={<ThankYou />} />
                    {/* Regalo Lucas page */}
                    {/* <Route path="/regalo-lucas" element={<RegaloLucas />} /> oculto mientras se trabaja */}
                    <Route
                      path="/membresia-telegram"
                      element={<MembresiaTelegram />}
                    />
                    <Route
                      path="/invitacion-privada"
                      element={<FullscreenExclusive />}
                    />
                    {/* AR: /ser-miembro — Mercado Pago + ARS. Intl: /membership — Stripe.
                        MembershipLandingRoute redirige visitantes al alias correcto
                        según geo (salvo ?_force_variant= para preview admin). */}
                    <Route
                      path="/ser-miembro"
                      element={<MembershipLandingRoute locale="es" />}
                    />
                    <Route
                      path="/membership"
                      element={<MembershipLandingRoute locale="intl" />}
                    />
                    {/* Backwards compat: /video-landing redirige al funnel canónico
                        (AR /ser-miembro · intl /membership). 301-like
                        en SPA — replace evita acumular history entries.
                        PRESERVA query string para no perder ?_force_variant=,
                        ?utm_*, ?ref=, etc. (componente inline porque <Navigate>
                        sin esto pierde el search del original). */}
                    <Route
                      path="/video-landing"
                      element={<LegacyVideoLandingRedirect />}
                    />
                    <Route
                      path="/exclusive"
                      element={<FullscreenExclusive />}
                    />
                    <Route
                      path="/exclusive-ar"
                      element={<FullscreenExclusiveAr />}
                    />
                    <Route
                      path="/exclusive-ar/success"
                      element={<ExclusivoArSuccess />}
                    />
                    <Route
                      path="/sorpresa"
                      element={<FullscreenSorpresa />}
                    />
                    {/* /sorprendeme desactivada — edge 404 via GLOBALLY_BLOCKED_PATHS + SPA redirect */}
                    <Route
                      path="/sorprendeme"
                      element={<Navigate to="/" replace />}
                    />
                    <Route
                      path="/sorprendeme/*"
                      element={<Navigate to="/" replace />}
                    />
                    <Route
                      path="/vip/:nombre"
                      element={<VipCustomPayment />}
                    />
                    <Route
                      path="/test-exclusivo"
                      element={<TestExclusivo />}
                    />
                    <Route
                      path="/bestfriend-instagram"
                      element={<BestFriendInstagram />}
                    />
                    <Route
                      path="/bestfriend-instagram/success"
                      element={<BestFriendInstagramSuccess />}
                    />
                    <Route
                      path="/whatsapp-broadcast"
                      element={<WhatsAppBroadcast />}
                    />
                    <Route
                      path="/instagram-mejores-amigos"
                      element={<InstagramMejoresAmigos />}
                    />
                    <Route
                      path="/terminos-y-condiciones"
                      element={<TerminosYCondiciones />}
                    />
                    <Route
                      path="/privacy-policy"
                      element={<PrivacyPolicy />}
                    />
                    <Route
                      path="/cookie-policy"
                      element={<CookiePolicy />}
                    />
                    <Route
                      path="/dar-de-baja"
                      element={<DarDeBaja />}
                    />
                    <Route
                      path="/portfolio"
                      element={<Portfolio />}
                    />
                    <Route
                      path="/mediakit-intro"
                      element={<MediaKitIntro />}
                    />
                    <Route
                      path="/mediakit"
                      element={
                        <MediaKitPasswordGate>
                          <MediaKitDesktop />
                        </MediaKitPasswordGate>
                      }
                    />
                    <Route
                      path="/audiovisual"
                      element={<Audiovisual />}
                    />
                    {/* ADD ALL CUSTOM ROUTES ABOVE THE CATCH-ALL "*" ROUTE */}
                    <Route path="*" element={<NotFound />} />
                  </Routes>
                      </PendingApprovalAccessGate>
                  <PostHogPageview />
                  <SessionRecordingEnforcer />
                  <SessionIngressTracker />
                  <ClerkPostHogIdentify />
                  <ClerkDataFastIdentify />
                  <GoogleAnalyticsPageview />
                  <PostHogTrackingVerification />
                  <SearchReferrerTracker />
                    </I18nLoadingWrapper>
                </CurrentTemplateProvider>
                  </AppClerkProvider>
              </BrowserRouter>
            </LanguageProvider>
          </RegionProvider>
        </TooltipProvider>
      </PostHogProvider>
    </QueryClientProvider>
  </ErrorBoundary>
  );

  return appContent;
};

export default App;
